import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, map, of, tap } from 'rxjs';
import * as Sentry from '@sentry/angular';
import { environment } from '../../../../environments/environment';
import { SaveErrorService } from './save-error.service';
import { EditLockService, type LockView } from './edit-lock.service';
import { partitionSavablePaths } from '../utils/editor-file-paths';

interface FileSaveResult {
	path: string;
	ok: boolean;
	error?: unknown;
}

export interface SaveFilesResult {
	/** Paths the server persisted — safe for the caller to drop from pending. */
	succeeded: string[];
	/** Paths the server failed to persist (kept pending so a retry re-sends only these). */
	failed: FileSaveResult[];
}

interface BatchSaveResponse {
	results: { path: string; ok: boolean; error?: string }[];
}

/** Flattens an unknown error into a plain object Sentry can display/search on. */
function serializeError(error: unknown): Record<string, unknown> {
	if (error instanceof HttpErrorResponse) {
		return { status: error.status, message: error.message, url: error.url };
	}
	return { message: String(error) };
}

// Corner-pin placement/action data can carry one entry per frame (thousands of
// {x,y} pairs) — the reason the backend's body-parser limit had to be raised
// to 15mb (see backend/src/index.ts). Sending that verbatim to Sentry Logs
// would blow past log size limits for no diagnostic value, so any array node
// over this size is replaced with a marker instead of being walked further.
const MAX_ARRAY_CHARS = 500;

/** Deep-walks a save payload, replacing any oversized array with a size marker so the rest of the (usually small) document is preserved as-is. */
function redactOversized(value: unknown): unknown {
	if (Array.isArray(value)) {
		const json = JSON.stringify(value);
		if (json.length > MAX_ARRAY_CHARS) return `TOO_LARGE_NOT_SENDING (${value.length} items, ${json.length} chars)`;
		return value.map(redactOversized);
	}
	if (value && typeof value === 'object') {
		return Object.fromEntries(Object.entries(value as Record<string, unknown>).map(([k, v]) => [k, redactOversized(v)]));
	}
	return value;
}

/** Payload of just the files that failed to save, for the Sentry log — succeeded files add nothing to a failure report. */
function payloadForLog(entries: Map<string, unknown>, failedPaths: string[]): Record<string, unknown> {
	const out: Record<string, unknown> = {};
	for (const path of failedPaths) out[path] = redactOversized(entries.get(path));
	return out;
}

@Injectable()
export class EditorDataService {
	private http = inject(HttpClient);
	private saveErrorService = inject(SaveErrorService);
	private editLock = inject(EditLockService);

	loadFile<T = unknown>(fetchPath: string): Observable<T> {
		return this.http.get<T>(`${environment.backendUrl}${fetchPath}`);
	}

	/**
	 * Saves every file for one project in a SINGLE batch request (one CORS
	 * preflight, one connection) instead of one PUT per file. This is what stops
	 * a large save from firing 2·N requests that saturate the browser's ~6
	 * connections-per-host budget — already shared with uploads, thumbnails and
	 * the media proxy in the same tab — where preflights would hang and cancel.
	 *
	 * The server writes each file independently and reports a per-file result,
	 * so one failure never fails the rest. Never errors: a whole-request network
	 * failure surfaces as every file `failed` (status 0).
	 */
	private runSaves(entries: Map<string, unknown>, trigger: string, create: boolean): Observable<SaveFilesResult> {
		const paths = [...entries.keys()];
		if (paths.length === 0) return of({ succeeded: [], failed: [] });

		// DEV-657: the slug used to be read off `paths[0]` alone, so ONE entry the
		// endpoint cannot address — a synthetic `chapter:<slug>` selection key —
		// sitting at the head of the insertion-ordered pending map failed every
		// valid file behind it. Partition instead: the unaddressable entries are
		// reported on their own and the rest of the batch still goes out.
		const { slug, savable, invalid } = partitionSavablePaths(paths);
		const invalidFailures: FileSaveResult[] = invalid.map((path) => ({ path, ok: false, error: 'Invalid file path' }));
		if (!slug) return of({ succeeded: [], failed: invalidFailures });

		// `create` marks every file in this batch as a document that does not
		// exist yet, which makes the backend write create-or-fail instead of
		// upsert. Without it a create whose slug collides with a document the
		// editor could not see — one no ref points at — replaces that document's
		// content outright (DEV-670).
		//
		// Only the savable paths go out: an entry the endpoint cannot address
		// fails on its own rather than taking the batch with it (DEV-657).
		const files = savable.map((path) => ({ path, data: entries.get(path), ...(create ? { create: true } : {}) }));

		// `trigger` lets the backend gate scene/image creation: only explicit
		// create flows (copy/add/drop) may upsert; `autosave` is update-only so a
		// mis-addressed save can't silently spawn a phantom document.
		return this.http.put<BatchSaveResponse>(`${environment.backendUrl}/projects/${slug}/batch`, { files, trigger }).pipe(
			map((resp): SaveFilesResult => ({
				succeeded: resp.results.filter((r) => r.ok).map((r) => r.path),
				failed: [...invalidFailures, ...resp.results.filter((r) => !r.ok).map((r) => ({ path: r.path, ok: false, error: r.error }))],
			})),
			// The whole request failed (network/5xx) → treat every file as failed.
			catchError((error: HttpErrorResponse) =>
				of<SaveFilesResult>({ succeeded: [], failed: [...invalidFailures, ...savable.map((path) => ({ path, ok: false, error }))] }),
			),
			tap((result) => {
				if (result.failed.length === 0) {
					this.saveErrorService.clear();
					return;
				}
				// Lock lost: a foreign lock / admin takeover rejected the whole batch
				// with 423. Flip the editor to read-only via the lock service and
				// suppress the generic "save failed" surface — this is expected, not
				// a fault. The fast detection path for an actively-typing user (the
				// heartbeat is the ≤30s backstop). See spec/plans/edit-locks/.
				const locked = result.failed.find(
					(f) => f.error instanceof HttpErrorResponse && f.error.status === 423,
				);
				if (locked) {
					const body = (locked.error as HttpErrorResponse).error as { lock?: LockView } | null;
					this.editLock.notifyLockLost(body?.lock ?? null);
					this.saveErrorService.clear();
					return;
				}
				this.saveErrorService.reportError(result.failed[0].error);
				// DEV-660: temporary granular reporting for the current save-bug
				// investigation — who/where/what/why for every failed save.
				Sentry.logger.error('Save failed', {
					projectSlug: slug,
					trigger,
					attemptedPaths: paths,
					failedPaths: result.failed.map((f) => f.path),
					payload: payloadForLog(entries, result.failed.map((f) => f.path)),
					error: serializeError(result.failed[0].error),
				});
			}),
		);
	}

	/**
	 * Backward-compatible batch save: emits once and ERRORS if any file failed,
	 * so existing `retry()` / error-callback call-sites keep working. Unlike the
	 * old `forkJoin`, individual requests are error-isolated and concurrency-
	 * limited — a single failure no longer cancels the rest of the batch.
	 *
	 * `trigger` identifies what caused this save (e.g. `'autosave'`,
	 * `'copy-scene'`) — carried through to the DEV-660 Sentry failure log.
	 *
	 * Pass `{ create: true }` when every file in the batch is a brand-new
	 * document, so the backend refuses rather than overwrites if the slug turns
	 * out to be occupied (DEV-670).
	 */
	saveFiles(entries: Map<string, unknown>, trigger: string, opts?: { create?: boolean }): Observable<SaveFilesResult> {
		return this.runSaves(entries, trigger, opts?.create === true).pipe(
			map((result) => {
				if (result.failed.length > 0) throw result.failed[0].error;
				return result;
			}),
		);
	}

	/**
	 * Resilient batch save: NEVER errors. Returns which files saved and which
	 * failed so the caller can clear only the saved ones from its pending set
	 * and retry just the rest — instead of re-firing the whole batch each time.
	 */
	saveFilesPartial(entries: Map<string, unknown>, trigger: string): Observable<SaveFilesResult> {
		// Always an edit of already-loaded documents (the autosave path), never a create.
		return this.runSaves(entries, trigger, false);
	}
}
